import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Container, Row, Col, Card, Table, Button, Alert } from 'react-bootstrap';
import callApi from "../../api/callApi";
import "./style.css";

const ViewExtractions = () => {
    const navigate = useNavigate();
    const [batchRequests, setBatchRequests] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        loadBatchRequests();
    }, []);

    const loadBatchRequests = async () => {
        try {
            setLoading(true);
            const url = `${process.env.REACT_APP_API_URL}/v1/portal/extract_statement/batch`;
            const response = await callApi(url, 'GET');
            
            if (response.data && response.data.requests) {
                setBatchRequests(response.data.requests);
            } else {
                setBatchRequests([]);
            }
        } catch (error) {
            console.error('Error loading batch requests:', error);
            setBatchRequests([]);
        } finally {
            setLoading(false);
        }
    };

    const formatDate = (dateString) => {
        if (!dateString) return dateString;
        try {
            const date = dateString.toDate ? dateString.toDate() : new Date(dateString._seconds ? dateString._seconds * 1000 : dateString);
            const day = date.getDate().toString().padStart(2, '0');
            const month = (date.getMonth() + 1).toString().padStart(2, '0');
            const year = date.getFullYear();
            return `${day}-${month}-${year}`; // DD-MM-YYYY format
        } catch (error) {
            return dateString;
        }
    };

    const handleDownloadPdf = async (requestId) => {
        try {
            const url = `${process.env.REACT_APP_API_URL}/v1/portal/download_statement/batch/${requestId}`;
            const blob = await callApi(url, 'GET', null, true); // blob=true for file download
            
            const downloadUrl = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = downloadUrl;
            a.download = `batch_statements_${requestId}.zip`;
            document.body.appendChild(a);
            a.click();
            window.URL.revokeObjectURL(downloadUrl);
            document.body.removeChild(a);
        } catch (error) {
            console.error('Error downloading PDF:', error);
            alert('Failed to download PDF files. Please try again.');
        }
    };

    const handleDownloadJson = async (requestId) => {
        await downloadExtractedStatement(requestId, 'json');
    };

    const handleDownloadXlsx = async (requestId) => {
        await downloadExtractedStatement(requestId, 'xlsx');
    };

    const downloadExtractedStatement = async (requestId, format) => {
        try {
            const url = `${process.env.REACT_APP_API_URL}/v1/portal/download_extracted_statement/batch/${requestId}?format=${format}`;
            const blob = await callApi(url, 'GET', null, true); // blob=true for file download
            
            const downloadUrl = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = downloadUrl;
            a.download = `batch_extracted_${requestId}.${format}`;
            document.body.appendChild(a);
            a.click();
            window.URL.revokeObjectURL(downloadUrl);
            document.body.removeChild(a);
        } catch (error) {
            console.error(`Error downloading ${format}:`, error);
            alert(`Failed to download ${format.toUpperCase()} file. Please try again.`);
        }
    };

    const handleView = (requestId) => {
        navigate(`/view-extractions/${requestId}`);
    };

    const clearAllHistory = async () => {
        if (window.confirm('Are you sure you want to delete all batch requests? This action cannot be undone.')) {
            try {
                const url = `${process.env.REACT_APP_API_URL}/v1/portal/extract_statement/batch`;
                const response = await callApi(url, 'DELETE');
                
                if (response.data) {
                    setBatchRequests([]);
                    alert('All batch requests have been deleted successfully.');
                } else {
                    alert('Failed to delete all requests. Please try again.');
                }
            } catch (error) {
                console.error('Error deleting all requests:', error);
                alert('Failed to delete all requests. Please try again.');
            }
        }
    };

    const clearSingleEntry = async (requestId) => {
        if (window.confirm('Are you sure you want to delete this batch request? This action cannot be undone.')) {
            try {
                const url = `${process.env.REACT_APP_API_URL}/v1/portal/extract_statement/batch/${requestId}`;
                const response = await callApi(url, 'DELETE');
                
                if (response.data) {
                    setBatchRequests(prev => prev.filter(req => req.request_id !== requestId));
                    // alert('Batch request has been deleted successfully.');
                } else {
                    alert('Failed to delete request. Please try again.');
                }
            } catch (error) {
                console.error('Error deleting request:', error);
                alert('Failed to delete request. Please try again.');
            }
        }
    };

    const getStatusBadgeClass = (status) => {
        switch (status) {
            case 'completed': return 'badge bg-success';
            case 'processing': return 'badge bg-secondary';
            case 'failed': return 'badge bg-danger';
            default: return 'badge bg-secondary';
        }
    };

    const getFilesSummary = (request) => {
        const processed = request.processed_files || 0;
        const extracted = request.extracted_files || 0;
        const valid = request.valid_files || 0;
        const invalid = request.invalid_files || 0;
        const failed = request.failed_files || 0;

        return {
            total: processed,
            extracted,
            valid,
            invalid,
            failed
        };
    };

    const getFileNames = (request) => {
        const jobs = request.jobs || {};
        const fileNames = Object.values(jobs).map(job => job.file_name).filter(Boolean);
        return fileNames;
    };

    const renderFileNames = (fileNames) => {
        if (fileNames.length === 0) return <span className="text-muted">No files</span>;
        
        return (
            <div>
                {fileNames.map((fileName, index) => (
                    <div key={index} className="text-truncate" style={{ maxWidth: '300px' }} title={fileName}>
                        {fileName}
                    </div>
                ))}
            </div>
        );
    };

    const handleUploadNavigation = () => {
        navigate('/data-extraction');
    };

    if (loading) {
        return (
            <Container fluid className="mt-4">
                <div className="text-center">
                    <div className="spinner-border text-secondary" role="status">
                        <span className="visually-hidden">Loading...</span>
                    </div>
                    <div className="mt-2 text-muted">Loading extraction history...</div>
                </div>
            </Container>
        );
    }

    return (
        <Container fluid className="mt-4 history-page">
            {/* Header */}
            <Row className="justify-content-center">
                <Col lg={12}>
                    <div className="text-center mb-5">
                        <h1 className="title mb-3">Data Extraction History</h1>
                    </div>
                </Col>
            </Row>

            {/* Data Extraction Table */}
            <Row className="justify-content-center">
                <Col lg={12}>
                    {/* Action Icons */}
                    <div className="d-flex justify-content-end mb-3 gap-4">
                        <i 
                            className="fas fa-sync-alt action-icon"
                            onClick={loadBatchRequests}
                            title="Refresh"
                        ></i>
                        <i 
                            className="fas fa-plus-circle action-icon upload-icon"
                            onClick={handleUploadNavigation}
                            title="New Batch Processing"
                        ></i>
                        {batchRequests.length > 0 && (
                            <i 
                                className="fas fa-trash-alt action-icon delete-files-icon"
                                onClick={clearAllHistory}
                                title="Delete All Batch Requests"
                            ></i>
                        )}
                    </div>
                    
                    <Card className="shadow-sm">
                        <Card.Body className="p-0">
                            {batchRequests.length === 0 ? (
                                <Alert variant="info" className="m-4 text-center">
                                    <i className="fas fa-info-circle me-2"></i>
                                    No upload history found. Start processing some PDF files to see them here.
                                </Alert>
                            ) : (
                                <div style={{ overflowX: 'auto' }}>
                                    <Table striped hover className="mb-0" style={{ textAlign: 'left', minWidth: '800px' }}>
                                        <thead className="table-success">
                                            <tr>
                                                <th style={{ textAlign: 'left', width: '120px' }}>Date</th>
                                                <th style={{ textAlign: 'left', width: '300px' }}>File Names</th>
                                                <th style={{ textAlign: 'center', width: '120px' }}>Status</th>
                                                <th style={{ textAlign: 'center', width: '120px' }}>Files</th>
                                                <th style={{ textAlign: 'center', width: '120px' }}>Actions</th>
                                                <th style={{ textAlign: 'center', width: '240px' }}>Downloads</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {batchRequests.map((request) => {
                                                const filesSummary = getFilesSummary(request);
                                                const isRequestProcessed = request.status === 'completed' || request.status === 'failed';
                                                const hasResultStored = request.result_stored || false;
                                                const fileNames = getFileNames(request);
                                                
                                                return (
                                                    <tr key={request.request_id}>
                                                        <td style={{ verticalAlign: 'middle' }}>
                                                            {formatDate(request.created_datetime)}
                                                        </td>
                                                        <td style={{ verticalAlign: 'middle', padding: '8px' }}>
                                                            {renderFileNames(fileNames)}
                                                        </td>
                                                        <td style={{ textAlign: 'center', verticalAlign: 'middle' }}>
                                                            <span className={getStatusBadgeClass(request.status)}>
                                                                {request.status.charAt(0).toUpperCase() + request.status.slice(1).toLowerCase() || 'Unknown'}
                                                            </span>
                                                        </td>
                                                        <td style={{ textAlign: 'center', verticalAlign: 'middle' }}>
                                                            <div className="small">
                                                                <div>Total: <strong>{filesSummary.total}</strong></div>
                                                                {filesSummary.extracted > 0 && (
                                                                    <div className="text-success">
                                                                        <i className="fas fa-check-circle me-1"></i>
                                                                        {filesSummary.extracted} processed
                                                                    </div>
                                                                )}
                                                                {filesSummary.failed > 0 && (
                                                                    <div className="text-danger">
                                                                        <i className="fas fa-times-circle me-1"></i>
                                                                        {filesSummary.failed} failed
                                                                    </div>
                                                                )}
                                                            </div>
                                                        </td>
                                                        <td style={{ textAlign: 'center', verticalAlign: 'middle' }}>
                                                            <div className="d-flex justify-content-center align-items-center action-icons-container">
                                                                <i 
                                                                    className="fas fa-eye view-icon"
                                                                    onClick={() => handleView(request.request_id)}
                                                                    title="View Details"
                                                                ></i>
                                                                <i 
                                                                    className="fas fa-trash-alt delete-icon"
                                                                    onClick={() => clearSingleEntry(request.request_id)}
                                                                    title="Delete This Batch"
                                                                ></i>
                                                            </div>
                                                        </td>
                                                        <td style={{ textAlign: 'center', verticalAlign: 'middle' }}>
                                                            <div className="d-flex gap-3 justify-content-center align-items-center">
                                                                <i 
                                                                    className={`fas fa-file-pdf download-icon pdf-icon ${!isRequestProcessed ? 'disabled' : ''}`}
                                                                    onClick={() => handleDownloadPdf(request.request_id)}
                                                                    title={isRequestProcessed ? "Download Original PDFs (ZIP)" : "Processing not complete"}
                                                                    style={{ cursor: !isRequestProcessed ? 'not-allowed' : 'pointer' }}
                                                                ></i>
                                                                <i 
                                                                    className={`fas fa-file-excel download-icon xlsx-icon ${!isRequestProcessed || !hasResultStored ? 'disabled' : ''}`}
                                                                    onClick={() => (isRequestProcessed && hasResultStored) ? handleDownloadXlsx(request.request_id) : null}
                                                                    title={isRequestProcessed && hasResultStored ? "Download Excel Output" : "No extracted data available"}
                                                                    style={{ cursor: (!isRequestProcessed || !hasResultStored) ? 'not-allowed' : 'pointer' }}
                                                                ></i>
                                                                <i 
                                                                    className={`fas fa-file-code download-icon json-icon ${!isRequestProcessed || !hasResultStored ? 'disabled' : ''}`}
                                                                    onClick={() => (isRequestProcessed && hasResultStored) ? handleDownloadJson(request.request_id) : null}
                                                                    title={isRequestProcessed && hasResultStored ? "Download JSON Output" : "No extracted data available"}
                                                                    style={{ cursor: (!isRequestProcessed || !hasResultStored) ? 'not-allowed' : 'pointer' }}
                                                                ></i>
                                                            </div>
                                                        </td>
                                                    </tr>
                                                );
                                            })}
                                        </tbody>
                                    </Table>
                                </div>
                            )}
                        </Card.Body>
                    </Card>
                </Col>
            </Row>
        </Container>
    );
};

export default ViewExtractions;
